import {
	MRT_GlobalFilterTextField,
	MRT_ToggleFiltersButton,
	MRT_ShowHideColumnsButton,
	MRT_ToggleDensePaddingButton,
	MRT_ToggleFullScreenButton,
} from "material-react-table";
import { Autocomplete, Box, Button, IconButton, TextField, lighten } from "@mui/material";
import {
	Delete,
	ContentCopy,
	Save,
	KeyboardDoubleArrowRight,
	RefreshOutlined,
	Map,
	Check,
	MoreHoriz,
} from "@mui/icons-material";

import type { JobsDashboardTableViewModel } from "../JobsDashboardTable.types";
import { PortalTooltip } from "../../../helpers";

export interface DashboardToolbarProps {
	vm: JobsDashboardTableViewModel;
	drawerOpen: boolean;
	setDrawerOpenState: (open: boolean) => void;
}

export function DashboardToolbar({ vm, drawerOpen, setDrawerOpenState }: DashboardToolbarProps) {
	return (
		<Box
			sx={(theme) => ({
				backgroundColor: lighten(theme.palette.background.default, 0.05),
				display: "flex",
				pl: "8px",
			})}
		>
			<div id="MRT-sub-components" className="horizontal-button-group">
				<MRT_GlobalFilterTextField table={vm.table} />
				<MRT_ToggleFiltersButton table={vm.table} />
				<MRT_ShowHideColumnsButton table={vm.table} />
				<MRT_ToggleDensePaddingButton table={vm.table} />
				<MRT_ToggleFullScreenButton table={vm.table} />
			</div>

			<div id="right-controls">
				{!drawerOpen && (
					<>
						<div id="company-view-dropdowns" className="hide-on-mobile-screen">
							<Autocomplete
								className="dashboard-dropdown"
								options={vm.views}
								value={vm.selectedView}
								disabled={false}
								groupBy={(option) => (option.group === "Default" ? "System Views" : option.group.toString())}
								getOptionKey={(option) => option.id}
								getOptionLabel={(option) => option.name}
								onChange={vm.onSavedViewChanged}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Saved View"
										placeholder="Select a view to load jobs"
										variant="outlined"
										size="small"
									/>
								)}
							/>
						</div>
						<div id="view-button" className="horizontal-button-group hide-on-small-screen">
							<PortalTooltip title="Refresh jobs">
								<span>
									<IconButton
										id="refresh-jobs-button"
										onClick={vm.refreshJobs}
										disabled={vm.noViewSelected}
										className="icon-button"
									>
										<RefreshOutlined fontSize="medium" style={{ color: vm.noViewSelected ? "grey" : "blue" }} />
									</IconButton>
								</span>
							</PortalTooltip>
							<PortalTooltip title="Save current view">
								<span>
									<IconButton
										id="save-view-button"
										onClick={vm.saveCurrentView}
										className="icon-button"
										disabled={vm.saveViewDisabled}
									>
										<Save fontSize="medium" style={{ color: vm.saveViewDisabled ? "grey" : "green" }} />
									</IconButton>
								</span>
							</PortalTooltip>
							<PortalTooltip title={vm.copyLinkTooltip}>
								<span>
									<IconButton
										id="copy-link-button"
										onClick={vm.copyLinkToClipboard}
										className="icon-button"
										disabled={vm.copyLinkDisabled}
									>
										<ContentCopy fontSize="medium" style={{ color: vm.copyLinkDisabled ? "grey" : "blue" }} />
									</IconButton>
								</span>
							</PortalTooltip>
							<PortalTooltip title="Delete view">
								<span>
									<IconButton
										id="delete-view-button"
										onClick={vm.deleteView}
										className="icon-button"
										disabled={vm.deleteViewDisabled}
									>
										<Delete fontSize="medium" style={{ color: vm.deleteViewDisabled ? "grey" : "red" }} />
									</IconButton>
								</span>
							</PortalTooltip>
						</div>
					</>
				)}
				<div id="toolbar-menu" className="horizontal-button-group">
					<div>
						<IconButton
							onClick={(event) => {
								vm.setMobileMenuElement(event.currentTarget);
							}}
							className="icon-button"
							id="toolbar-menu-button"
						>
							<MoreHoriz />
						</IconButton>
					</div>
				</div>
				<div id="big-blue-buttons" className="horizontal-button-group">
					<div id="mark-ready-buttons" className="multi-screen-size-button-group hide-on-small-screen">
						<Button
							id="mark-ready-button"
							className="text-button big-screen-button"
							onClick={() => {
								vm.setMarkJobsReadyVisible(true);
							}}
							color="primary"
							variant="contained"
						>
							{vm.getMarkReadyText(vm.unreadySelectedJobs)}
						</Button>
						<PortalTooltip title={vm.getMarkReadyText(vm.unreadySelectedJobs)}>
							<span>
								<IconButton
									className="icon-button medium-screen-button"
									onClick={() => {
										vm.setMarkJobsReadyVisible(true);
									}}
								>
									<Check fontSize="medium" />
								</IconButton>
							</span>
						</PortalTooltip>
					</div>
					<div id="show-on-map-buttons" className="multi-screen-size-button-group">
						<Button
							id="show-on-map-button"
							onClick={vm.showOnMap}
							className="text-button big-screen-button"
							color="primary"
							variant="contained"
							disabled={
								Object.keys(vm.selectionDisplayedOnMap).length === Object.keys(vm.rowSelection).length &&
								Object.keys(vm.rowSelection).every((key) => !!vm.selectionDisplayedOnMap[key])
							}
						>
							{vm.getShowMapText()}
						</Button>
						<PortalTooltip title={vm.getShowMapText()}>
							<span>
								<IconButton className="icon-button medium-screen-button" onClick={vm.showOnMap}>
									<Map fontSize="medium" />
								</IconButton>
							</span>
						</PortalTooltip>
						{drawerOpen ? (
							<IconButton
								onClick={() => {
									setDrawerOpenState(!drawerOpen);
								}}
								className="icon-button"
								id="right-drawer-button"
								disabled={vm.noViewSelected}
							>
								<KeyboardDoubleArrowRight fontSize="large" style={{ color: vm.noViewSelected ? "grey" : undefined }} />
							</IconButton>
						) : (
							<></>
						)}
					</div>
				</div>
			</div>
		</Box>
	);
}
